import axios from 'axios';
import { API_BASE_URL } from './config';

const materialsAPI = axios.create({
  baseURL: `${API_BASE_URL}/materials`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
materialsAPI.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface Material {
  _id?: string;
  id?: string;
  name: string;
  category: string;
  unit: string;
  currentStock: number;
  minStock: number;
  maxStock: number;
  unitCost: number;
  supplier: string;
  location: string;
  project?: string;
  description?: string;
  specifications?: Record<string, any>;
  lastRestocked?: Date;
  status: 'in-stock' | 'low-stock' | 'out-of-stock' | 'on-order';
  stockHistory?: any[];
  createdAt?: Date;
  updatedAt?: Date;
}

export interface MaterialFilters {
  category?: string;
  status?: string;
  supplier?: string;
  location?: string;
  project?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface StockUpdate {
  quantity: number;
  type: 'add' | 'remove' | 'set';
  reason?: string;
  cost?: number;
}

export const materialsService = {
  // Get all materials with filters
  getAll: async (filters?: MaterialFilters) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    const response = await materialsAPI.get(`/?${params.toString()}`);
    // Backend returns an object: { materials, totalPages, currentPage, total }
    // Return just the materials array for simpler consumption in pages
    return response.data?.materials ?? [];
  },

  // Get material by ID
  getById: async (id: string) => {
    const response = await materialsAPI.get(`/${id}`);
    return response.data;
  },

  // Create new material
  create: async (material: Omit<Material, '_id' | 'id' | 'createdAt' | 'updatedAt'>) => {
    const response = await materialsAPI.post('/', material);
    return response.data;
  },

  // Update material
  update: async (id: string, material: Partial<Material>) => {
    const response = await materialsAPI.put(`/${id}`, material);
    return response.data;
  },

  // Delete material
  delete: async (id: string) => {
    const response = await materialsAPI.delete(`/${id}`);
    return response.data;
  },

  // Update stock
  updateStock: async (id: string, stockUpdate: StockUpdate) => {
    const response = await materialsAPI.patch(`/${id}/stock`, stockUpdate);
    return response.data;
  },

  // Get materials by project
  getByProject: async (projectId: string) => {
    const response = await materialsAPI.get(`/project/${projectId}`);
    return response.data;
  },

  // Get low stock materials
  getLowStock: async () => {
    const response = await materialsAPI.get('/low-stock');
    return response.data;
  },

  // Get materials statistics
  getStatistics: async () => {
    const response = await materialsAPI.get('/statistics');
    return response.data;
  },

  // Get stock history
  getStockHistory: async (id: string) => {
    const response = await materialsAPI.get(`/${id}/stock-history`);
    return response.data;
  },

  // Bulk update materials
  bulkUpdate: async (updates: Array<{ id: string; data: Partial<Material> }>) => {
    const response = await materialsAPI.patch('/bulk-update', { updates });
    return response.data;
  },

  // Get materials by category
  getByCategory: async (category: string) => {
    const response = await materialsAPI.get(`/category/${category}`);
    return response.data;
  },

  // Get materials by supplier
  getBySupplier: async (supplier: string) => {
    const response = await materialsAPI.get(`/supplier/${encodeURIComponent(supplier)}`);
    return response.data;
  },

  // Reorder material (set status to on-order)
  reorder: async (id: string, quantity: number, expectedDate?: Date) => {
    const response = await materialsAPI.patch(`/${id}/reorder`, { quantity, expectedDate });
    return response.data;
  },
};

export default materialsService;